import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { DollarSign, Flame, Clock, BarChart3, ArrowLeftRight, ClipboardCheck } from 'lucide-react';
import { sans, mono, green, greenLight, greenDark, red, amber, coral, coralSoft, useTheme, useCurrency } from '../tokens';
import { useTranslation, useLanguage } from '../i18n';
import { fmtC, fmtCompactC } from '../helpers';
import { BUDGET_BY_DEPT, getSpendByParentDept, GL_MONTHS } from '../data/companyGL';
import { computeRunwayMonth } from '../data/cashflowForecast';

// ─── Mini KPI Card (investor-style with icon + hover) ───────────────────────────
function MiniKpi({ label, value, sub, color, icon: Icon }) {
  const t = useTheme();
  return (
    <div style={{
      padding: '14px 16px',
      borderLeft: `3px solid ${color || t.border}`,
    }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {Icon && <Icon size={16} color={color || t.textMuted} style={{ opacity: 0.6 }} />}
        <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontWeight: 600, fontFamily: sans }}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, fontFamily: mono, color: color || t.textPrimary, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, fontFamily: sans }}>{sub}</div>}
    </div>
  );
}

// ─── Section wrapper (collapsible, draggable) ──────────────────────────────────
function DashSection({ title, children, span, onMoveUp, onMoveDown, canMoveUp, canMoveDown, onTitleClick, dragKey, onDragStart, onDragOver, onDrop, isDragOver, defaultOpen = true }) {
  const t = useTheme();
  const [sectionHovered, setSectionHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div
      draggable={!!dragKey}
      onDragStart={dragKey ? (e) => { e.dataTransfer.setData('text/plain', dragKey); onDragStart?.(dragKey); } : undefined}
      onDragOver={dragKey ? (e) => { e.preventDefault(); onDragOver?.(dragKey); } : undefined}
      onDrop={dragKey ? (e) => { e.preventDefault(); onDrop?.(dragKey); } : undefined}
      onDragEnd={dragKey ? () => onDragStart?.(null) : undefined}
      onMouseEnter={() => dragKey && setSectionHovered(true)}
      onMouseLeave={() => dragKey && setSectionHovered(false)}
      style={{
        background: t.bgCard,
        border: `1px solid ${isDragOver ? green : t.border}`,
        borderLeft: `3px solid ${isDragOver ? coral : coralSoft}`,
        borderRadius: 10,
        padding: '20px 24px',
        gridColumn: span ? `span ${span}` : undefined,
        cursor: dragKey ? 'grab' : undefined,
        opacity: isDragOver ? 0.7 : 1,
        transition: 'border-color 0.15s, opacity 0.15s',
      }}
    >
      <div
        onClick={() => setIsOpen(prev => !prev)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isOpen ? 14 : 0, cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {dragKey && (
            <span style={{
              fontSize: 10, cursor: 'grab', userSelect: 'none',
              color: sectionHovered ? t.textMuted : 'transparent',
              transition: 'color 0.15s',
            }} title="Drag to reorder">::</span>
          )}
          <div
            onClick={onTitleClick ? (e) => { e.stopPropagation(); onTitleClick(); } : undefined}
            style={{
              fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
              color: t.textMuted, fontFamily: sans,
              cursor: onTitleClick ? 'pointer' : 'default',
            }}
          >{title}</div>
        </div>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      <div style={{
        maxHeight: isOpen ? 2000 : 0,
        overflow: 'hidden',
        opacity: isOpen ? 1 : 0,
        transition: 'max-height 0.3s ease, opacity 0.2s ease',
      }}>
        {children}
      </div>
    </div>
  );
}

// ─── Small table row ────────────────────────────────────────────────────────────
function TableRow({ cells, header }) {
  const t = useTheme();
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: cells.map(c => c.width || '1fr').join(' '),
      gap: 8, padding: header ? '6px 0' : '6px 0',
      borderBottom: `1px solid ${header ? t.border : t.borderLight}`,
      fontSize: header ? 9 : 11,
      fontWeight: header ? 700 : 400,
      color: header ? green : t.textPrimary,
      textTransform: header ? 'uppercase' : 'none',
      letterSpacing: header ? 0.5 : 0,
      fontFamily: header ? sans : mono,
    }}>
      {cells.map((c, i) => (
        <span key={i} style={{ textAlign: c.align || 'left', color: c.color || undefined, fontFamily: c.mono ? mono : undefined }}>{c.value}</span>
      ))}
    </div>
  );
}

// ─── Burn Rate Panel with multi-select Net/Gross ─────────────────────────────────
const BURN_NET_COLOR = '#3B82F6';   // blue (matches investor headcount)
const BURN_GROSS_COLOR = '#F97316'; // orange (matches investor gross burn)

function BurnRatePanel({ burnMonths, burnValuesNet, burnValuesOperating, closedOperating, inProgressLabels, burnInflows, eomBalances, cc, t, ...props }) {
  const [showNet, setShowNet] = useState(() => {
    try { const v = localStorage.getItem('burnToggleNet'); return v !== null ? v === 'true' : true; } catch { return true; }
  });
  const [showOperating, setShowOperating] = useState(() => {
    // localStorage key kept as 'burnToggleGross' for backward compat — no
    // sense forcing every existing user back to defaults on a label change.
    try { const v = localStorage.getItem('burnToggleGross'); return v !== null ? v === 'true' : false; } catch { return false; }
  });

  // Cash-basis burn (BOM minus EOM on 11xxx). Net = all flows; Operating
  // = same but excluding journal entries that touch equity (3xxxx) — i.e.
  // strips out raises and equity distributions so the trend reflects pure
  // operating burn for a pre-revenue company.
  const datasets = [];
  if (showNet) {
    datasets.push({
      label: 'Net Cash Burn',
      data: burnValuesNet,
      borderColor: BURN_NET_COLOR,
      backgroundColor: showOperating ? 'transparent' : BURN_NET_COLOR + '15',
      fill: !showOperating,
      tension: 0.3, pointRadius: 4, pointBackgroundColor: BURN_NET_COLOR,
      pointBorderColor: '#fff', pointBorderWidth: 1.5, pointHitRadius: 8, borderWidth: 2,
    });
  }
  if (showOperating) {
    datasets.push({
      label: 'Operating Burn',
      data: burnValuesOperating,
      borderColor: BURN_GROSS_COLOR,
      backgroundColor: BURN_GROSS_COLOR + '10',
      fill: true,
      tension: 0.3, pointRadius: 4, pointBackgroundColor: BURN_GROSS_COLOR,
      pointBorderColor: '#fff', pointBorderWidth: 1.5, pointHitRadius: 8, borderWidth: 2,
      borderDash: showNet ? [5, 3] : [],
    });
  }
  // Average line — Operating burn over CLOSED months only ("closed" =
  // months ≤ the last close period the user selected in Close & Review).
  // closedOperating is computed by the parent so the chart, the KPI card,
  // and the top-strip KPI all use the same closed-month set.
  const avgSrc = (closedOperating && closedOperating.length) ? closedOperating : burnValuesOperating;
  const avgRaw = avgSrc.reduce((s, v) => s + v, 0) / avgSrc.length;
  const avgColor = BURN_GROSS_COLOR;
  datasets.push({
    label: '6-mo avg',
    data: burnMonths.map(() => avgRaw),
    borderColor: avgColor + '50', borderWidth: 1, borderDash: [3, 4],
    pointRadius: 0, pointHoverRadius: 0, fill: false, tension: 0,
  });

  const chartRef = useRef(null);
  const [avgLabelY, setAvgLabelY] = useState(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart && chart.scales && chart.scales.y) {
      const px = chart.scales.y.getPixelForValue(avgRaw);
      setAvgLabelY(px);
    }
  }, [avgRaw, showNet, showOperating]);

  const toggleNet = () => {
    if (!showNet || showOperating) {
      const next = !showNet;
      setShowNet(next);
      try { localStorage.setItem('burnToggleNet', next); } catch {}
    }
  };
  const toggleOperating = () => {
    if (!showOperating || showNet) {
      const next = !showOperating;
      setShowOperating(next);
      try { localStorage.setItem('burnToggleGross', next); } catch {}
    }
  };

  return (
    <DashSection title="Burn Rate Trend (6 mo)" span={2} {...props}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
        <button onClick={toggleNet} style={{
          padding: '2px 10px', borderRadius: 10, fontSize: 10, fontFamily: sans,
          border: `1px solid ${showNet ? BURN_NET_COLOR : t.border}`,
          background: showNet ? BURN_NET_COLOR + '18' : 'transparent',
          color: showNet ? BURN_NET_COLOR : t.textMuted,
          fontWeight: showNet ? 600 : 400, cursor: 'pointer',
        }}>
          Net
        </button>
        <button onClick={toggleOperating} style={{
          padding: '2px 10px', borderRadius: 10, fontSize: 10, fontFamily: sans,
          border: `1px solid ${showOperating ? BURN_GROSS_COLOR : t.border}`,
          background: showOperating ? BURN_GROSS_COLOR + '18' : 'transparent',
          color: showOperating ? BURN_GROSS_COLOR : t.textMuted,
          fontWeight: showOperating ? 600 : 400, cursor: 'pointer',
        }}>
          Operating
        </button>
        <span style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, marginLeft: 4 }}>
          {showNet && showOperating ? 'Net = full BOM−EOM | Operating = pure operating burn' :
           showNet ? 'BOM − EOM full-ledger view. Includes unrealized FX revaluation on USD-held cash — paper movement, not real cash.' :
           'Pure operating burn — excludes raises (3xxxx), revenue/grants (4xxxx), and interest income (7xxxx)'}
        </span>
        {inProgressLabels && inProgressLabels.length > 0 && (
          <span style={{ fontSize: 9, color: amber, fontFamily: sans, marginLeft: 8, fontStyle: 'italic' }}>
            ⓘ {inProgressLabels.join(', ')} not yet closed — running totals only, excluded from average
          </span>
        )}
      </div>
      <div style={{ height: 165, position: 'relative' }}>
        {avgLabelY != null && (
          <div style={{
            position: 'absolute', top: avgLabelY - 14, right: 8, zIndex: 2,
            fontSize: 9, fontFamily: mono, color: avgColor,
            opacity: 0.7,
          }}>
            avg {fmtCompactC(avgRaw, cc)}
          </div>
        )}
        <Line ref={chartRef} data={{ labels: burnMonths, datasets }} options={{
          animation: { duration: 800, easing: 'easeOutQuart' },
          responsive: true, maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              filter: (item) => item.dataset.label !== '6-mo avg',
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${fmtC(ctx.raw, cc)}`,
                afterBody: (items) => {
                  const idx = items[0]?.dataIndex;
                  if (idx == null) return [];
                  const lines = [
                    `BOM Cash: ${fmtC(eomBalances[idx], cc)}`,
                    `EOM Cash: ${fmtC(eomBalances[idx + 1], cc)}`,
                  ];
                  if (showOperating && burnInflows[idx] > 0) {
                    lines.push(`Inflows: ${fmtC(burnInflows[idx], cc)}`);
                  }
                  return lines;
                },
              },
            },
          },
          scales: {
            y: {
              // Fixed scale so toggling Net/Operating doesn't jump the axis.
              // Range covers normal PMD burn (~$400k-$1.6M) plus headroom
              // for FX-driven Net spikes. Raise months may clip — tooltip
              // shows the real number.
              min: 0,
              max: 2000000,
              ticks: { callback: v => fmtCompactC(v, cc), color: t.textMuted, font: { size: 9, family: mono } },
              grid: { color: t.borderLight },
              border: { display: false },
            },
            x: { ticks: { color: t.textMuted, font: { size: 9, family: sans }, maxRotation: 0 }, grid: { display: false }, border: { display: false } },
          },
        }} />
      </div>
    </DashSection>
  );
}

// ─── Budget Utilization with hover tooltip ───────────────────────────────────────
function BudgetUtilSection({ deptUtil, cc, t, onNavigate, ...props }) {
  const [hovered, setHovered] = useState(null);
  const [period, setPeriod] = useState('ytd');
  const [customRange, setCustomRange] = useState({ start: '2025-01', end: '2026-02' });

  // Compute budget utilization for any period
  const activeDeptUtil = (() => {
    if (period === 'ytd') return deptUtil;

    // Get month indices for the selected period
    const indices = getMonthIndicesForPeriod(period, customRange);
    const numMonths = indices.length || 1;
    const spendData = getSpendByParentDept(indices);

    return deptUtil.map(d => {
      const spend = spendData.find(s => s.dept === d.dept);
      // Pro-rate annual budget by number of months
      const proRatedBudget = Math.round(d.budget * numMonths / 12);
      const actual = spend ? spend.total : 0;
      return { dept: d.dept, util: proRatedBudget ? Math.round(actual / proRatedBudget * 100) : 0, budget: proRatedBudget };
    }).sort((a, b) => b.util - a.util);
  })();

  // Overall utilization
  const totalBudget = activeDeptUtil.reduce((s, d) => s + d.budget, 0);
  const totalActual = activeDeptUtil.reduce((s, d) => s + Math.round(d.budget * d.util / 100), 0);
  const overallUtil = totalBudget ? Math.round(totalActual / totalBudget * 100) : 0;

  // Softer colours — green theme consistent
  const barColor = (util) => util > 100 ? '#E07070' : util > 90 ? '#D4A76A' : green;

  const renderRow = (d, isOverall) => {
    const actual = Math.round(d.budget * d.util / 100);
    return (
      <div key={d.dept}
        onMouseEnter={() => setHovered(d.dept)}
        onMouseLeave={() => setHovered(null)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: isOverall ? '6px 0' : '3px 0',
          borderBottom: isOverall ? `1px solid ${t.borderLight}` : 'none',
          marginBottom: isOverall ? 4 : 0,
          position: 'relative',
        }}>
        <span style={{
          fontSize: isOverall ? 11 : 10, fontFamily: sans,
          color: isOverall ? t.textPrimary : t.textSecondary,
          fontWeight: isOverall ? 600 : 400,
          width: 130, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{d.dept}</span>
        <span style={{
          fontSize: 10, fontFamily: mono, fontWeight: 600, width: 36, textAlign: 'right', flexShrink: 0,
          color: d.util > 100 ? red : d.util > 90 ? amber : t.textPrimary,
        }}>{d.util}%</span>
        <div style={{ flex: 1, position: 'relative', height: isOverall ? 12 : 8 }}>
          {/* Track */}
          <div style={{ position: 'absolute', inset: 0, background: t.borderLight, borderRadius: 5 }} />
          {/* Fill */}
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            borderRadius: 5, width: `${Math.min(d.util, 100)}%`,
            background: barColor(d.util), transition: 'width 0.3s',
          }} />
          {/* 100% reference line */}
          <div style={{
            position: 'absolute', right: 0, top: -2, bottom: -2,
            width: 1.5, background: t.textMuted + '50', borderRadius: 1,
          }} />
        </div>
        {hovered === d.dept && (
          <div style={{
            position: 'absolute', top: -36, left: 140, zIndex: 10,
            background: '#1a1a1a', color: '#fff', padding: '6px 10px',
            borderRadius: 6, fontSize: 10, fontFamily: mono,
            whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex', gap: 12,
          }}>
            <span>Budget: {fmtCompactC(d.budget, cc)}</span>
            <span>Actual: {fmtCompactC(actual, cc)}</span>
            <span style={{ color: d.util > 100 ? '#ef9a9a' : d.util > 90 ? '#ffcc80' : '#a5d6a7' }}>
              {d.util > 100 ? `Over by ${fmtCompactC(actual - d.budget, cc)}` : `${100 - d.util}% remaining`}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <DashSection title="Budget Utilization" span={2} {...props} onTitleClick={onNavigate ? () => onNavigate('budget') : undefined}>
      {/* Period toggle */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {[{ key: 'mtd', label: 'MTD' }, { key: 'ytd', label: 'YTD' }, { key: 'custom', label: 'Custom' }].map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)} style={{
            padding: '2px 10px', borderRadius: 12, fontSize: 10, fontFamily: sans,
            border: `1px solid ${period === p.key ? green : t.border}`,
            background: period === p.key ? greenLight : 'transparent',
            color: period === p.key ? greenDark : t.textSecondary,
            fontWeight: period === p.key ? 600 : 400,
            cursor: 'pointer',
          }}>{p.label}</button>
        ))}
        {period === 'mtd' && (
          <span style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, alignSelf: 'center', marginLeft: 4 }}>
            Mar 2026 -- no data yet
          </span>
        )}
        {period === 'custom' && (
          <DateRangeSelector start={customRange.start} end={customRange.end} onChange={setCustomRange} t={t} />
        )}
      </div>
      {/* Overall bar */}
      {renderRow({ dept: 'Overall', util: overallUtil, budget: totalBudget }, true)}
      {/* Per department */}
      <div style={{ position: 'relative' }}>
        {activeDeptUtil.map(d => renderRow(d, false))}
      </div>
    </DashSection>
  );
}

// ─── USD/CAD Rate Tracker ────────────────────────────────────────────────────────
function FxRateTracker({ usdAssets, cc, t, onRateLoaded, ...props }) {
  const [rateData, setRateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiForecast, setAiForecast] = useState(null);
  const [showDrivers, setShowDrivers] = useState(false);

  // Try to fetch AI forecast from backend (daily, cached 24h)
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    fetch(`${BACKEND_URL}/fx/forecast`, { signal: AbortSignal.timeout(5000) })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.forecast) setAiForecast(data.forecast);
      })
      .catch(() => {}); // Backend not available -- use static assumptions
  }, []);

  useEffect(() => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 120); // fetch 120 days for 90 trading days (better model calibration)
    const fmt = d => d.toISOString().slice(0, 10);
    // Safety timeout -- don't stay on loading forever
    const timeout = setTimeout(() => setLoading(false), 5000);
    fetch(`https://api.frankfurter.app/${fmt(from)}..${fmt(today)}?from=USD&to=CAD`)
      .then(r => r.json())
      .then(data => {
        clearTimeout(timeout);
        if (data.rates) {
          const entries = Object.entries(data.rates)
            .map(([date, r]) => ({ date, rate: r.CAD }))
            .sort((a, b) => a.date.localeCompare(b.date))
            .slice(-90);
          setRateData(entries);
          if (entries.length > 0 && onRateLoaded) {
            onRateLoaded(entries[entries.length - 1].rate);
          }
        }
        setLoading(false);
      })
      .catch(() => { clearTimeout(timeout); setLoading(false); });
  }, []);

  if (loading && !rateData) return (
    <DashSection title="USD/CAD Rate" span={2} {...props}>
      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, padding: 20, textAlign: 'center' }}>Loading rates...</div>
    </DashSection>
  );

  if (!rateData || rateData.length === 0) return (
    <DashSection title="USD/CAD Rate" span={2} {...props}>
      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, padding: 20, textAlign: 'center' }}>Rate data unavailable</div>
    </DashSection>
  );

  const current = rateData[rateData.length - 1];
  const weekAgo = rateData[Math.max(0, rateData.length - 6)] || rateData[0];
  const monthAgo = rateData[0];
  const change7d = current.rate - weekAgo.rate;
  const change30d = current.rate - monthAgo.rate;
  const pct7d = (change7d / weekAgo.rate * 100).toFixed(2);
  const pct30d = (change30d / monthAgo.rate * 100).toFixed(2);

  // 10-day moving average
  const maWindow = 10;
  const maData = rateData.map((_, i) => {
    if (i < maWindow - 1) return null;
    const slice = rateData.slice(i - maWindow + 1, i + 1);
    return slice.reduce((s, d) => s + d.rate, 0) / maWindow;
  });

  // ─── Holt-Winters DES with auto-optimized parameters ────────────────────
  // Uses 90 days of data, grid-searches alpha/beta for best fit, damped trend
  // Reference: github.com/evrimakgul/Exchange_Rate_Prediction
  const projDays = 7;
  const rates = rateData.map(d => d.rate);
  // Show only last 30 days on chart but use all 90 for model training
  const displayDays = 30;
  const displayData = rateData.slice(-displayDays);

  // Grid search for optimal alpha, beta (minimize MSE on last 20% of data)
  const trainSize = Math.floor(rates.length * 0.8);
  let bestAlpha = 0.3, bestBeta = 0.05, bestMSE = Infinity;
  for (let a = 0.1; a <= 0.6; a += 0.1) {
    for (let b = 0.01; b <= 0.15; b += 0.02) {
      let l = rates[0], tr = rates[1] - rates[0];
      let mse = 0, count = 0;
      for (let i = 1; i < rates.length; i++) {
        const forecast = l + 0.9 * tr;
        l = a * rates[i] + (1 - a) * forecast;
        tr = b * (l - (l - a * (rates[i] - forecast))) + (1 - b) * 0.9 * tr;
        if (i >= trainSize) { mse += (rates[i] - forecast) ** 2; count++; }
      }
      mse = count ? mse / count : Infinity;
      if (mse < bestMSE) { bestMSE = mse; bestAlpha = a; bestBeta = b; }
    }
  }

  const phi = 0.9; // damping factor
  let level = rates[0];
  let trend = rates.length > 1 ? (rates[Math.min(4, rates.length - 1)] - rates[0]) / Math.min(4, rates.length - 1) : 0;
  const residuals = [];
  const fittedValues = [];

  for (let i = 1; i < rates.length; i++) {
    const prevLevel = level;
    const prevTrend = trend;
    const forecast = prevLevel + phi * prevTrend;
    level = bestAlpha * rates[i] + (1 - bestAlpha) * forecast;
    trend = bestBeta * (level - prevLevel) + (1 - bestBeta) * phi * prevTrend;
    residuals.push(rates[i] - forecast);
    fittedValues.push(forecast);
  }

  // MAE and standard deviation for confidence bands
  const residStd = Math.sqrt(residuals.slice(-30).reduce((s, r) => s + r * r, 0) / 30);
  const mae = residuals.slice(-30).reduce((s, r) => s + Math.abs(r), 0) / 30;

  // Project forward with damped trend
  const projLabels = Array.from({ length: projDays }, (_, i) => {
    const d = new Date(current.date);
    d.setDate(d.getDate() + i + 1);
    return d.toISOString().slice(5, 10);
  });
  const projRates = [];
  const projUpper80 = [];
  const projLower80 = [];
  let cumulativePhi = 0;
  for (let h = 1; h <= projDays; h++) {
    cumulativePhi += Math.pow(phi, h);
    const forecast = level + cumulativePhi * trend;
    const interval80 = 1.28 * residStd * Math.sqrt(h); // 80% CI (tighter, more useful)
    projRates.push(forecast);
    projUpper80.push(forecast + interval80);
    projLower80.push(forecast - interval80);
  }

  const forecastDay7 = projRates[projRates.length - 1];

  // Use last 30 days for display, recalculate MA for display window
  const displayMa = displayData.map((_, i) => {
    const globalIdx = rateData.length - displayDays + i;
    if (globalIdx < maWindow - 1) return null;
    const slice = rateData.slice(globalIdx - maWindow + 1, globalIdx + 1);
    return slice.reduce((s, d) => s + d.rate, 0) / maWindow;
  });

  const labels = [...displayData.map(d => d.date.slice(5)), ...projLabels];
  const trendUp = change7d > 0;

  const chartData = {
    labels,
    datasets: [
      {
        label: 'USD/CAD',
        data: [...displayData.map(d => d.rate), ...Array(projDays).fill(null)],
        borderColor: '#6B9E85',
        backgroundColor: 'rgba(107, 158, 133, 0.08)',
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 3,
        borderWidth: 2,
      },
      {
        label: '10-day MA',
        data: [...displayMa, ...Array(projDays).fill(null)],
        borderColor: '#B0B0B0',
        borderDash: [4, 3],
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 1.2,
        fill: false,
      },
      {
        label: 'Forecast',
        data: [...Array(displayDays - 1).fill(null), current.rate, ...projRates],
        borderColor: '#D4A76A',
        borderDash: [3, 3],
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 1.5,
        fill: false,
      },
      {
        label: '80% upper',
        data: [...Array(displayDays - 1).fill(null), current.rate, ...projUpper80],
        borderColor: 'rgba(212, 167, 106, 0.25)',
        backgroundColor: 'rgba(212, 167, 106, 0.06)',
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 0.8,
        fill: false,
        borderDash: [2, 2],
      },
      {
        label: '80% lower',
        data: [...Array(displayDays - 1).fill(null), current.rate, ...projLower80],
        borderColor: 'rgba(212, 167, 106, 0.25)',
        backgroundColor: 'rgba(212, 167, 106, 0.06)',
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 0.8,
        fill: '-1',
        borderDash: [2, 2],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (items) => items[0]?.label ? `2026-${items[0].label}` : '',
          label: (ctx) => ctx.parsed.y != null ? `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(4)}` : null,
        },
      },
    },
    scales: {
      x: {
        display: true,
        ticks: { font: { size: 8, family: mono }, color: t.textMuted, maxTicksLimit: 8 },
        grid: { display: false },
      },
      y: {
        display: true,
        ticks: { font: { size: 8, family: mono }, color: t.textMuted, callback: v => v.toFixed(2) },
        grid: { color: t.borderLight },
      },
    },
  };

  const arrow = trendUp ? '\u25B2' : '\u25BC';
  const trendColor = trendUp ? '#D98A8A' : '#8BB8A8'; // CAD weakens = red (bad for CAD company), strengthens = green

  return (
    <DashSection title="USD/CAD Rate" span={2} {...props}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>
            {current.rate.toFixed(4)}
          </div>
          <div style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>
            1 USD = {current.rate.toFixed(4)} CAD &middot; {current.date}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, fontFamily: mono, color: trendColor }}>
            {arrow} {Math.abs(change7d).toFixed(4)} ({pct7d}%)
            <span style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, marginLeft: 4 }}>7d</span>
          </div>
          <div style={{ fontSize: 10, fontFamily: mono, color: change30d > 0 ? '#D98A8A' : '#8BB8A8', marginTop: 2 }}>
            {change30d > 0 ? '\u25B2' : '\u25BC'} {Math.abs(change30d).toFixed(4)} ({pct30d}%)
            <span style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, marginLeft: 4 }}>30d</span>
          </div>
          <div style={{ fontSize: 10, fontFamily: mono, color: '#D4A76A', marginTop: 4, borderTop: `1px solid ${t.borderLight}`, paddingTop: 4 }}>
            7d forecast: {forecastDay7.toFixed(4)}
            <span style={{ fontSize: 8, color: t.textMuted, fontFamily: sans, marginLeft: 2 }}>
              ({projLower80[projDays-1].toFixed(4)}-{projUpper80[projDays-1].toFixed(4)})
            </span>
          </div>
        </div>
      </div>

      <div style={{ height: 120 }}>
        <Line data={chartData} options={chartOptions} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <div style={{ fontSize: 9, color: t.textMuted, fontFamily: sans }}>
          <span style={{ color: '#6B9E85' }}>&mdash;</span> Daily rate &nbsp;
          <span style={{ color: '#B0B0B0' }}>- -</span> 10-day avg &nbsp;
          <span style={{ color: '#D4A76A' }}>- -</span> Forecast &nbsp;
          <span style={{ color: 'rgba(212, 167, 106, 0.4)' }}>///</span> 80% CI
        </div>
        <div style={{ fontSize: 9, color: t.textMuted, fontFamily: sans }}>
          USD exposure: {fmtCompactC(usdAssets, cc)} &middot; &plusmn;$0.01 = &plusmn;{fmtCompactC(usdAssets * 0.01, cc)}
        </div>
      </div>
      <div style={{ fontSize: 8, color: t.textFaint, fontFamily: sans, marginTop: 4, textAlign: 'right' }}>
        Rates: ECB via frankfurter.app | Forecast: Holt-Winters DES (90-day training, auto-optimized)
      </div>

      {/* Forecast drivers — collapsed by default */}
      <div style={{ marginTop: 8, borderTop: `1px solid ${t.borderLight}`, paddingTop: 6 }}>
        <button
          onClick={() => setShowDrivers(v => !v)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4,
            color: t.textMuted, fontFamily: sans,
          }}
        >
          <span style={{ fontSize: 8 }}>{showDrivers ? '▲' : '▼'}</span>
          Forecast drivers
        </button>
        {showDrivers && (
          <div style={{ marginTop: 5 }}>
            {(() => {
              const hasRealAI = aiForecast?.confidence && aiForecast.confidence !== 'low';
              const factors = hasRealAI && aiForecast?.factors?.length > 1
                ? aiForecast.factors
                : [
                  'WTI oil prices — #1 CAD driver, declining oil weakens CAD',
                  'BoC vs Fed rate differential — wider spread attracts capital to USD',
                  'US-Canada trade policy & tariffs — uncertainty strengthens USD',
                  'Geopolitical sentiment — central bank signals, economic data releases',
                  'Holt-Winters DES model — 90-day statistical trend with macro adjustment',
                ];
              return factors.map((f, i) => (
                <div key={i} style={{ fontSize: 9, color: t.textFaint, fontFamily: sans, padding: '2px 0', display: 'flex', gap: 6 }}>
                  <span style={{ color: t.textMuted, flexShrink: 0, width: 10, textAlign: 'right' }}>{i + 1}.</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{f}</span>
                </div>
              ));
            })()}
            {aiForecast?.direction && aiForecast.confidence !== 'low' && (
              <div style={{ marginTop: 4, fontSize: 9, fontFamily: mono, color: aiForecast.direction === 'bullish_cad' ? green : aiForecast.direction === 'bearish_cad' ? red : t.textMuted }}>
                AI signal: {aiForecast.direction.replace('_', ' ').toUpperCase()} (confidence: {aiForecast.confidence})
              </div>
            )}
          </div>
        )}
      </div>
    </DashSection>
  );
}

// ─── Spend by Department (GL-derived, parent departments) ────────────────────────
const SPEND_PERIOD_OPTIONS = [
  { key: 'mtd', label: 'MTD' },
  { key: '3m', label: 'Last 3 mo' },
  { key: '6m', label: 'Last 6 mo' },
  { key: 'ytd', label: 'YTD 2026' },
  { key: 'custom', label: 'Custom' },
];

function getMonthIndicesForPeriod(periodKey, customRange) {
  // GL_MONTHS: 0=Jan25 .. 13=Feb26
  if (periodKey === 'custom' && customRange) {
    const startIdx = GL_MONTHS.indexOf(customRange.start);
    const endIdx = GL_MONTHS.indexOf(customRange.end);
    if (startIdx >= 0 && endIdx >= 0 && endIdx >= startIdx) {
      return Array.from({ length: endIdx - startIdx + 1 }, (_, i) => startIdx + i);
    }
    return [];
  }
  switch (periodKey) {
    case 'mtd': return [];
    case '3m': return [11, 12, 13];
    case '6m': return [8, 9, 10, 11, 12, 13];
    case 'ytd': return [12, 13];
    default: return [8, 9, 10, 11, 12, 13];
  }
}

// Month label for selectors
const MONTH_OPTIONS = GL_MONTHS.map(m => {
  const [y, mo] = m.split('-');
  const label = new Date(Number(y), Number(mo) - 1).toLocaleDateString('en', { month: 'short', year: '2-digit' });
  return { value: m, label };
});

function DateRangeSelector({ start, end, onChange, t }) {
  const inputStyle = {
    padding: '3px 8px', borderRadius: 6, fontSize: 10, fontFamily: sans,
    border: `1px solid ${t.border}`, background: t.bgCard, color: t.textPrimary,
    cursor: 'pointer', outline: 'none',
  };
  // Convert YYYY-MM to YYYY-MM-DD for date input, and back
  const toDate = (ym) => ym + '-01';
  const toMonth = (d) => d ? d.slice(0, 7) : '';

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 6 }}>
      <input type="date" value={toDate(start)} min="2025-01-01" max="2026-02-28"
        onChange={e => { const m = toMonth(e.target.value); if (m) onChange({ start: m, end: m > end ? m : end }); }}
        style={inputStyle} />
      <span style={{ fontSize: 9, color: t.textMuted }}>to</span>
      <input type="date" value={toDate(end)} min={toDate(start)} max="2026-02-28"
        onChange={e => { const m = toMonth(e.target.value); if (m) onChange({ start, end: m }); }}
        style={inputStyle} />
    </div>
  );
}

function SpendByDepartment({ cc, t, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) {
  const [selectedDept, setSelectedDept] = useState(null);
  const [period, setPeriod] = useState('6m');
  const [customRange, setCustomRange] = useState({ start: '2025-01', end: '2026-02' });

  const monthIndices = getMonthIndicesForPeriod(period, customRange);
  const depts = getSpendByParentDept(monthIndices);
  const maxTotal = depts[0]?.total || 1;
  const selectedData = depts.find(d => d.dept === selectedDept);

  // Softer bar colour
  const barColor = '#A8C5B8';
  const barColorActive = '#6B9E85';

  return (
    <DashSection title="Spend by Department" span={2} onMoveUp={onMoveUp} onMoveDown={onMoveDown} canMoveUp={canMoveUp} canMoveDown={canMoveDown}>
      {/* Period selector */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {SPEND_PERIOD_OPTIONS.map(p => (
          <button key={p.key} onClick={() => { setPeriod(p.key); setSelectedDept(null); }} style={{
            padding: '3px 10px', borderRadius: 12, fontSize: 10, fontFamily: sans,
            border: `1px solid ${period === p.key ? green : t.border}`,
            background: period === p.key ? greenLight : 'transparent',
            color: period === p.key ? greenDark : t.textSecondary,
            fontWeight: period === p.key ? 600 : 400,
            cursor: 'pointer',
          }}>
            {p.label}
          </button>
        ))}
        {period === 'custom' && (
          <DateRangeSelector start={customRange.start} end={customRange.end} onChange={r => { setCustomRange(r); setSelectedDept(null); }} t={t} />
        )}
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        {/* Department list */}
        <div style={{ flex: 1 }}>
          {depts.map(d => (
            <div key={d.dept}
              onClick={() => setSelectedDept(selectedDept === d.dept ? null : d.dept)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px',
                cursor: 'pointer', borderRadius: 4,
                background: selectedDept === d.dept ? greenLight : 'transparent',
                transition: 'background 0.1s',
              }}>
              <span style={{
                fontSize: 10, fontFamily: sans, color: selectedDept === d.dept ? greenDark : t.textSecondary,
                fontWeight: selectedDept === d.dept ? 600 : 400,
                width: 130, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{d.dept}</span>
              <div style={{ flex: 1, height: 8, background: t.borderLight, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 4,
                  width: `${(d.total / maxTotal) * 100}%`,
                  background: selectedDept === d.dept ? barColorActive : barColor,
                  transition: 'width 0.3s',
                }} />
              </div>
              <span style={{ fontSize: 9, fontFamily: mono, color: t.textMuted, width: 50, textAlign: 'right', flexShrink: 0 }}>
                {fmtCompactC(d.total, cc)}
              </span>
            </div>
          ))}
        </div>

        {/* Account drilldown */}
        <div style={{
          width: 260, borderLeft: `1px solid ${t.borderLight}`, paddingLeft: 12,
          overflowY: 'auto', maxHeight: 260,
        }}>
          {selectedData ? (
            <>
              <div style={{ fontSize: 10, fontWeight: 600, color: greenDark, fontFamily: sans, marginBottom: 6 }}>
                {selectedDept}
              </div>
              {selectedData.accounts.map((a, i) => {
                const pct = Math.round(a.amount / selectedData.total * 100);
                return (
                  <div key={i} style={{ padding: '3px 0', borderBottom: `1px solid ${t.borderLight}` }}>
                    <div style={{ fontSize: 10, fontFamily: sans, color: t.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.name}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, fontFamily: mono, color: t.textSecondary }}>{fmtCompactC(a.amount, cc)}</span>
                      <span style={{ fontSize: 9, fontFamily: mono, color: t.textMuted }}>{pct}%</span>
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop: 6, fontSize: 9, fontFamily: mono, color: t.textMuted, textAlign: 'right' }}>
                Total: {fmtCompactC(selectedData.total, cc)}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, padding: '20px 0', textAlign: 'center' }}>
              Click a department to see account breakdown
            </div>
          )}
        </div>
      </div>
    </DashSection>
  );
}

// ─── Dashboard Settings Panel ────────────────────────────────────────────────────
function DashboardSettings({ config, panelLabels, kpiLabels, allPanels, allKpis, onTogglePanel, onToggleKpi, onMovePanel, onSetPanelOrder, onReset, onClose }) {
  const t = useTheme();
  const [draggedKey, setDraggedKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const visibleOrder = config.panelOrder.filter(k => !config.hiddenPanels.includes(k));

  const checkboxStyle = (checked) => ({
    width: 16, height: 16, borderRadius: 3, cursor: 'pointer',
    border: `1.5px solid ${checked ? green : t.textMuted}`,
    background: checked ? green : 'transparent',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, marginRight: 10, transition: 'all 0.15s',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
      <div style={{
        position: 'relative', background: t.bgCard, borderRadius: 12,
        padding: '28px 32px', width: 420, maxHeight: '80vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: `1px solid ${t.border}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: sans }}>Customize Dashboard</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: t.textMuted, padding: '4px 8px' }}>x</button>
        </div>

        {/* KPI Cards */}
        <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, marginBottom: 10, fontFamily: sans }}>KPI Cards</div>
        <div style={{ marginBottom: 20 }}>
          {allKpis.map(key => {
            const visible = !config.hiddenKpis.includes(key);
            return (
              <div key={key} onClick={() => onToggleKpi(key)} style={{
                display: 'flex', alignItems: 'center', padding: '7px 0', cursor: 'pointer',
                fontSize: 13, fontFamily: sans, color: visible ? t.textPrimary : t.textMuted,
              }}>
                <div style={checkboxStyle(visible)}>
                  {visible && <span style={{ color: '#fff', fontSize: 11, lineHeight: 1 }}>&#10003;</span>}
                </div>
                {kpiLabels[key]}
              </div>
            );
          })}
        </div>

        {/* Widgets */}
        <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, marginBottom: 10, fontFamily: sans }}>Widgets</div>
        <div style={{ marginBottom: 20 }}>
          {config.panelOrder.map(key => {
            const visible = !config.hiddenPanels.includes(key);
            const isDragging = draggedKey === key;
            const isOver = dragOverKey === key;
            return (
              <div
                key={key}
                draggable={visible}
                onDragStart={visible ? () => setDraggedKey(key) : undefined}
                onDragEnd={() => { setDraggedKey(null); setDragOverKey(null); }}
                onDragOver={visible && draggedKey && draggedKey !== key ? (e) => { e.preventDefault(); setDragOverKey(key); } : undefined}
                onDrop={visible && draggedKey && draggedKey !== key ? (e) => {
                  e.preventDefault();
                  const from = visibleOrder.indexOf(draggedKey);
                  const to = visibleOrder.indexOf(key);
                  if (from === -1 || to === -1) return;
                  const next = [...visibleOrder];
                  next.splice(from, 1);
                  next.splice(to, 0, draggedKey);
                  onSetPanelOrder(next);
                  setDraggedKey(null);
                  setDragOverKey(null);
                } : undefined}
                style={{
                  display: 'flex', alignItems: 'center', padding: '7px 0',
                  fontSize: 13, fontFamily: sans, color: visible ? t.textPrimary : t.textMuted,
                  opacity: isDragging ? 0.4 : 1,
                  borderTop: isOver && !isDragging ? `2px solid ${green}` : '2px solid transparent',
                  transition: 'opacity 0.15s',
                }}
              >
                {visible && (
                  <span
                    title="Drag to reorder"
                    style={{ fontSize: 14, color: t.textMuted, cursor: 'grab', marginRight: 8, userSelect: 'none', lineHeight: 1 }}
                  >
                    &#8942;&#8942;
                  </span>
                )}
                {!visible && <span style={{ width: 22, marginRight: 8 }} />}
                <div onClick={() => onTogglePanel(key)} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1 }}>
                  <div style={checkboxStyle(visible)}>
                    {visible && <span style={{ color: '#fff', fontSize: 11, lineHeight: 1 }}>&#10003;</span>}
                  </div>
                  {panelLabels[key]}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={onReset} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 12, color: t.textMuted, fontFamily: sans, textDecoration: 'underline',
          }}>Reset to defaults</button>
          <button onClick={onClose} style={{
            background: green, color: '#fff', border: 'none', borderRadius: 6,
            padding: '8px 20px', fontSize: 12, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
          }}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────────
function ControllerDashboard({ monthData, taskList, mockToday, onNavigate, sbData, onLiveRateLoaded }) {
  const t = useTheme();
  const cc = useCurrency();
  const tr = useTranslation();
  const lang = useLanguage();
  const closeMonthLabel = (() => {
    const d = mockToday ? new Date(mockToday) : new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { month: 'short', year: 'numeric' });
  })();

  // ─── Supabase data (fallback to hardcoded while loading) ───────────────
  const sb = sbData?.data;
  const sbMetrics = sb?.metrics || {};

  // Cash & runway — preference: live Flinks bank feed > Supabase metrics >
  // hardcoded. The bank-feed values come through /api/bank-feed/flinks/
  // cash-position and refresh on the finkloz-bank-feed-updated event
  // (fired by FlinksConnections after Connect/Sync/Disconnect).
  const [bankFeedPosition, setBankFeedPosition] = useState(null);
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    const COMPANY_ID = '00000000-0000-0000-0000-000000000000';
    const fetchPos = () => {
      fetch(`${BACKEND_URL}/api/bank-feed/flinks/cash-position?company_id=${COMPANY_ID}`)
        .then(r => (r.ok ? r.json() : null))
        .then(data => { if (data?.connected) setBankFeedPosition(data); else setBankFeedPosition(null); })
        .catch(() => setBankFeedPosition(null));
    };
    fetchPos();
    const onUpdate = () => fetchPos();
    window.addEventListener('finkloz-bank-feed-updated', onUpdate);
    return () => window.removeEventListener('finkloz-bank-feed-updated', onUpdate);
  }, []);

  const liq = sbMetrics.liquidity || {};
  const totalLiquidity = bankFeedPosition?.totals_usd?.total
    ?? liq.total ?? 35900000;
  const cashInBank = bankFeedPosition?.totals_usd?.cash
    ?? liq.cash_in_bank ?? 1822903;
  // monthlyBurn, runwayMonths, runwayColor computed below after burnValuesNet

  // Close progress
  const done = taskList.filter(t => t.done).length;
  const total = taskList.length;
  const reviewed = taskList.filter(t => t.reviewed).length;
  const overdue = taskList.filter(t => !t.done && !t.blocked && t.dueDate < mockToday).length;
  const blocked = taskList.filter(t => t.blocked).length;
  const closePct = Math.round((done / total) * 100);

  // Burn trend — sliding 6-month window, cash-basis (BOM minus EOM on
  // cash + investment accounts). Real values from synced sage_gl_detail;
  // when a month isn't synced, fall back to historical CFS values below.
  const sbBurn = sbMetrics.burn_data || {};

  // Historical fallback in USD, keyed YYYY-MM. Originally tracked CAD net
  // cash decrease per month-end CFS; converted here using BoC month-end
  // rates so the fallback is unit-consistent with the live endpoint.
  // Used only when sage_gl_detail has no rows for that month.
  const burnFallbackMap = {
    '2025-09': 296243,    // 412,400 CAD / 1.3921
    '2025-10': 524944,    // 735,865 / 1.4018
    '2025-11': 800008,    // 1,118,391 / 1.3979
    '2025-12': 2120965,   // 2,907,003 / 1.3706
    '2026-01': 1550068,   // 2,102,028 / 1.3562  (will be overridden by live)
    '2026-02': 144140,    // 196,626 / 1.3642    (will be overridden by live)
  };

  // Last fully-closed month, sourced from the Close & Review tab.
  // mockToday is set to the 4th of the month *after* selectedMonth.
  // We use mockToday's month directly (no -1 step-back) so the burn chart
  // is always one month ahead of Close & Review's selectedMonth — that's
  // a temporary offset until the Close & Review tab is fully built out
  // and properly tracks the real close cadence (PMD's books close ahead).
  // When Close & Review is done, change to `d.setMonth(d.getMonth() - 1)`.
  const lastClosedYM = (() => {
    const d = mockToday ? new Date(mockToday) : new Date();
    d.setDate(1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();

  // Compute the trailing 6-month window ending at the CURRENT month
  // (in-progress, partial data). Anchored to wall-clock today so it slides
  // forward as the calendar advances. The latest bar is partial — today's
  // running total — which is intentional for a real-time CFO view.
  const burnWindow = (() => {
    const ref = new Date();
    ref.setDate(1); // current month, no step back
    const out = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(ref);
      d.setMonth(d.getMonth() - i);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      out.push({
        ym: `${y}-${m}`,
        label: d.toLocaleDateString('en-US', { month: 'short' }),
      });
    }
    return out;
  })();
  const burnMonths = burnWindow.map(w => w.label);
  const burnMonthsYM = burnWindow.map(w => w.ym);

  // Live cash-basis burn from synced sage_gl_detail. Keyed by YYYY-MM with
  // both flavours: net = BOM-EOM (all activity) and operating = BOM-EOM
  // excluding journal entries that touch equity (3xxxx) accounts so raises
  // and equity distributions don't distort the trend. Re-fetched on mount
  // and after any "Sync Sage" click.
  const [liveBurn, setLiveBurn] = useState({}); // { 'YYYY-MM': { net, operating } }
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    const COMPANY_ID = '00000000-0000-0000-0000-000000000000';
    const monthsParam = burnMonthsYM.join(',');
    const fetchLive = () => {
      fetch(`${BACKEND_URL}/api/sage/burn-trend?company_id=${COMPANY_ID}&months=${monthsParam}`)
        .then(r => (r.ok ? r.json() : null))
        .then(rows => {
          if (!Array.isArray(rows)) return;
          const map = {};
          for (const r of rows) {
            if (r.has_data) {
              map[r.month] = {
                net: r.net_burn_usd,
                operating: r.operating_burn_usd,
              };
            }
          }
          setLiveBurn(map);
        })
        .catch(() => {});
    };
    fetchLive();
    const onSync = () => fetchLive();
    window.addEventListener('finkloz-sage-sync-completed', onSync);
    return () => window.removeEventListener('finkloz-sage-sync-completed', onSync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [burnMonthsYM.join(',')]);

  // Per-month: prefer live Sage GL values; fall back to historical CFS map.
  // For unsynced months we don't have an "operating" view, so operating
  // falls back to the same value as net.
  const burnValuesNet = burnMonthsYM.map(ym => {
    const live = liveBurn[ym];
    if (live && live.net != null) return live.net;
    return burnFallbackMap[ym] || 0;
  });
  const burnValuesOperating = burnMonthsYM.map(ym => {
    const live = liveBurn[ym];
    if (live && live.operating != null) return live.operating;
    return burnFallbackMap[ym] || 0;
  });
  // KPIs use Operating burn averaged over CLOSED months only. "Closed" is
  // defined by the Close & Review tab — anything after lastClosedYM is
  // partial / not yet booked, and would drag the average down, making
  // runway look artificially long. Chart still plots all 6 months so you
  // can see the running totals for unclosed months at a glance.
  const closedMask = burnMonthsYM.map(ym => ym <= lastClosedYM);
  const closedOperating = burnValuesOperating.filter((_, i) => closedMask[i]);
  const inProgressLabels = burnMonths.filter((_, i) => !closedMask[i]);
  const monthlyBurn = closedOperating.length
    ? Math.round(closedOperating.reduce((s, v) => s + v, 0) / closedOperating.length)
    : Math.round(burnValuesOperating.reduce((s, v) => s + v, 0) / burnValuesOperating.length);
  // Runway — forward-looking from the FP&A forecast (not trailing avg). Walks
  // the forecast month by month from today, EXCLUDING Series B / C raise
  // inflows. Returns the actual month cash hits 0 ("Jul 2028" etc.) so the
  // KPI matches what the board asks: "what's our cash-out date if no raise?"
  const runwayProjection = computeRunwayMonth(totalLiquidity);
  const runwayMonths = runwayProjection.monthsUntil;
  // Color thresholds based on the months-until value (still useful for color
  // even when display shows the date). null = survives the whole forecast →
  // green. Otherwise red/amber/green based on runway length.
  const runwayColor = runwayMonths == null
    ? green
    : runwayMonths <= 12 ? red
    : runwayMonths <= 24 ? amber
    : green;

  // burnInflows kept for tooltip backward-compat but always 0 on cash-basis
  // — net already includes inflows, operating excludes equity flows.
  const burnInflows = burnMonthsYM.map(() => 0);
  const eomBalances = sbBurn.eom_balances || [44490000, 43850000, 42810000, 40170000, 37390000, 35480000, 35290000];

  // Budget utilization by parent department (grouped per org chart)
  // Each parent sums its child departments from BUDGET_BY_DEPT
  const PARENT_DEPTS = [
    { dept: 'Operations', children: ['operations', 'machine-shop', 'cleanroom', 'supply-chain', 'facilities'] },
    { dept: 'Mechanical R&D', children: ['design', 'mech-design'] },
    { dept: 'Electrical & Software', children: ['elec-software'] },
    { dept: 'Clinical & Regulatory', children: ['clinical-reg'] },
    { dept: 'Design Control & Testing', children: ['dc-testing'] },
    { dept: 'Quality', children: ['quality'] },
    { dept: 'IT', children: ['it'] },
    { dept: 'IP', children: ['ip'] },
    { dept: 'Finance & Legal', children: ['finance'] },
    { dept: 'People & Culture', children: ['people'] },
    { dept: 'Executive', children: ['executive'] },
  ];
  const deptUtil = PARENT_DEPTS.map(({ dept, children }) => {
    const budget = children.reduce((s, k) => s + (BUDGET_BY_DEPT[k] || []).reduce((ss, r) => ss + r.budget, 0), 0);
    const actual = children.reduce((s, k) => s + (BUDGET_BY_DEPT[k] || []).reduce((ss, r) => ss + r.actual, 0), 0);
    return { dept, util: budget ? Math.round(actual / budget * 100) : 0, budget };
  }).sort((a, b) => b.util - a.util);

  // Prime rates — from Supabase or fallback
  const sbRates = sbMetrics.prime_rates || {};
  const cadPrime = sbRates.cad_prime || 4.95;
  const usPrime = sbRates.us_prime || 7.50;
  // cadSavingsRate/usSavingsRate now come from GIC records directly

  // GIC & Investment Schedule — from Supabase
  const gics = (sb?.gics || []).map(g => {
    const principal = Number(g.principal);
    const rateNum = Number(g.rate_percent);
    const maturityDate = g.maturity_date || null;
    const lastInterestDate = g.last_interest_date || null;
    // Interest at maturity: principal × rate × (maturity − last_interest_date) / 365
    // null for savings accounts (no maturity date)
    let interestAtMaturity = null;
    if (maturityDate && lastInterestDate && rateNum > 0) {
      const days = Math.max(0, (new Date(maturityDate) - new Date(lastInterestDate)) / 86400000);
      interestAtMaturity = Math.round(principal * (rateNum / 100) * (days / 365));
    }
    return {
      bank: g.institution,
      currency: g.currency,
      amount: principal,
      rate: `${rateNum.toFixed(2)}%`,
      rateNum,
      maturity: maturityDate,
      status: g.account_type === 'Savings' ? 'Savings' : undefined,
      interestAtMaturity,
    };
  });
  const today = new Date();
  const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  // Filter out matured GICs (maturity date in the past), keep savings (no maturity)
  const activeGics = gics.filter(g => !g.maturity || new Date(g.maturity) >= today);
  const sortedGics = [...activeGics].sort((a, b) => {
    if (!a.maturity && !b.maturity) return 0;
    if (!a.maturity) return 1;
    if (!b.maturity) return -1;
    return new Date(a.maturity) - new Date(b.maturity);
  });
  const getMaturityColor = (maturity) => {
    if (!maturity) return t.textMuted;
    const d = new Date(maturity);
    if (d <= thirtyDaysFromNow) return amber;
    return green;
  };

  // Accrued interest — computed from Supabase GIC data
  const vmLastPaid = today.getDate() >= 16
    ? new Date(today.getFullYear(), today.getMonth(), 16)
    : new Date(today.getFullYear(), today.getMonth() - 1, 16);

  const accruedInterest = (sb?.gics || []).reduce((total, g) => {
    const principal = Number(g.principal);
    const rate = Number(g.rate_percent) / 100;
    let lastPaidDate;
    if (g.interest_frequency === 'monthly_16th') {
      lastPaidDate = vmLastPaid;
    } else {
      lastPaidDate = g.last_interest_date ? new Date(g.last_interest_date) : new Date();
    }
    const daysSince = Math.max(0, Math.floor((today - lastPaidDate) / (1000 * 60 * 60 * 24)));
    return total + (principal * rate / 365 * daysSince);
  }, 0);

  // Receivables — from Supabase, with live accrued interest override
  const receivables = (sb?.receivables || []).map(r => ({
    name: r.account_name,
    code: r.gl_code,
    amount: r.gl_code === '12500' ? Math.round(accruedInterest) : Number(r.amount),
  }));

  // AP aging — computed from Supabase ap_aging table
  // Vendor original currencies (from GL transaction analysis)
  // Amounts in DB are CAD-equivalent (vendor aging report converts at report date)
  const VENDOR_CURRENCIES = {
    'Globex Components': 'USD',
    'Initech Tooling': 'USD',
    'Umbrella Labs': 'USD',
    'Stark Manufacturing': 'USD',
    'Wayne Logistics': 'USD',
    'Cyberdyne Systems': 'USD',
    'Hooli Materials': 'USD',
    'Vandelay Imports': 'USD',
    'Pied Piper Components': 'USD',
    'Soylent Industries': 'USD',
    'Acme Supplies': 'USD',
    'Wonka Logistics': 'USD',
    'Gekko Instruments': 'USD',
    'Tyrell Devices': 'USD',
    'Oscorp Materials': 'USD',
    'Massive Dynamic': 'USD',
    'Nakatomi Trading': 'USD',
    'Abstergo Labs': 'USD',
    'Euro Precision GmbH': 'EUR',
    'Rhein Electronic GmbH': 'EUR',
    'Continental Logic SAS': 'EUR',
    'Asia Industrial Co., Ltd.': 'USD',
    'Alpine Tooling AG': 'CHF',
  };
  const reportFxRate = cc.fxRate || 1.3718; // Rate used in vendor aging report
  const EUR_CAD = 1.53; // approximate EUR/CAD
  const CHF_CAD = 1.57; // approximate CHF/CAD
  const displayCurr = cc.currency || 'CAD';

  const getOrigCurrency = (vendor) => VENDOR_CURRENCIES[vendor] || 'CAD';
  const toOrigAmount = (cadAmt, origCurr) => {
    if (origCurr === 'CAD') return cadAmt;
    if (origCurr === 'USD') return cadAmt / reportFxRate;
    if (origCurr === 'EUR') return cadAmt / EUR_CAD;
    if (origCurr === 'CHF') return cadAmt / CHF_CAD;
    return cadAmt;
  };
  const toDisplayAmount = (cadAmt) => {
    if (displayCurr === 'CAD') return cadAmt;
    return cadAmt / reportFxRate; // CAD -> USD
  };

  const apRaw = sb?.apAging || [];
  const apBucketVendors = {};
  const apBucketTotals = {};
  apRaw.forEach(row => {
    if (!apBucketVendors[row.bucket]) apBucketVendors[row.bucket] = [];
    apBucketVendors[row.bucket].push({
      vendor: row.vendor_name,
      amount: Number(row.amount), // CAD amount from report
      origCurrency: getOrigCurrency(row.vendor_name),
    });
    apBucketTotals[row.bucket] = (apBucketTotals[row.bucket] || 0) + Number(row.amount);
  });
  const allBuckets = ['Current', '31-60', '61-90', '91-120', '121+'];
  const apAging = allBuckets.map(b => ({ bucket: b, amount: apBucketTotals[b] || 0 }));
  const apTotal = apAging.reduce((s, a) => s + a.amount, 0);
  // Overdue = vendors in non-Current buckets, grouped by vendor
  const overdueMap = {};
  apRaw.filter(r => r.bucket !== 'Current').forEach(r => {
    if (!overdueMap[r.vendor_name]) overdueMap[r.vendor_name] = { vendor: r.vendor_name, total: 0 };
    const amt = Number(r.amount);
    overdueMap[r.vendor_name].total += amt;
    if (r.bucket === '121+') overdueMap[r.vendor_name].past121 = amt;
    else overdueMap[r.vendor_name].past31 = (overdueMap[r.vendor_name].past31 || 0) + amt;
  });
  const apOverdue = Object.values(overdueMap).sort((a, b) => b.total - a.total);
  const [expandedBucket, setExpandedBucket] = useState(null);
  const [showAllOverdue, setShowAllOverdue] = useState(false);
  const apCurrentPct = apTotal ? Math.round((apAging[0].amount / apTotal) * 100) : 0;
  const apPastDue = apTotal - apAging[0].amount;

  // FX exposure — preference: live Sage GL (unrealized) + bank feed (USD
  // asset total) > Supabase metrics > hardcoded. Unrealized FX comes from
  // Sage account 70020 YTD (real today). USD assets in CAD comes from bank
  // feed when connected; until then we fall back to the hardcoded value.
  const [liveFx, setLiveFx] = useState(null);
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    const COMPANY_ID = '00000000-0000-0000-0000-000000000000';
    const fetchFx = () => {
      fetch(`${BACKEND_URL}/api/sage/fx-exposure?company_id=${COMPANY_ID}&fy=2026`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d?.available) setLiveFx(d); })
        .catch(() => {});
    };
    fetchFx();
    const onSync = () => fetchFx();
    window.addEventListener('finkloz-sage-sync-completed', onSync);
    return () => window.removeEventListener('finkloz-sage-sync-completed', onSync);
  }, []);

  const sbFx = sbMetrics.fx_exposure || {};

  // USD assets in CAD — from bank feed if connected, else hardcoded.
  // bankFeedPosition exposes per-currency native amounts; total USD * FX rate
  // gives the CAD-denominated FX exposure (= "how much CAD value is at risk
  // to USD/CAD movement").
  const bankFeedUsdNative = (
    (bankFeedPosition?.totals_native_by_ccy?.cash?.USD || 0)
    + (bankFeedPosition?.totals_native_by_ccy?.investments?.USD || 0)
  );
  const fxRateForExposure = liveFx?.fx_rate_used || 1.37;
  const usdAssets = bankFeedUsdNative > 0
    ? Math.round(bankFeedUsdNative * fxRateForExposure)
    : (sbFx.usd_assets || 38200000);

  // Unrealized FX YTD: live from Sage GL when available. Sign reversed
  // because account 70020 is debit-normal (debit = loss, credit = gain),
  // and the KPI is shown as a positive "loss" value historically.
  const unrealizedFX = liveFx?.unrealized_fx_ytd_cad != null
    ? Math.round(liveFx.unrealized_fx_ytd_cad)
    : (sbFx.unrealized_fx || 1791355);

  // ─── Dashboard config — persisted in localStorage ────────────────────────
  const CONFIG_KEY = 'dashboard-config';
  const ALL_PANELS = ['burn', 'budget', 'gic', 'fx', 'receivables', 'ap', 'close'];
  const ALL_KPIS = ['cash', 'burn', 'runway', 'budgetUtil', 'fxExposure', 'monthEnd'];
  const PANEL_LABELS = { burn: 'Burn Rate Trend', budget: 'Budget Utilization', gic: 'GIC & Investments', fx: 'USD/CAD Rate', receivables: 'Grants & Receivables', ap: 'Accounts Payable Aging', close: 'Month-End Close' };
  const KPI_LABELS = { cash: 'Cash & Investments', burn: 'Monthly Burn', runway: 'Runway', budgetUtil: 'Budget Utilization', fxExposure: 'FX Exposure', monthEnd: 'Month-End' };

  const [dashConfig, setDashConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(CONFIG_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Migrate: ensure all panels/kpis exist
        const order = parsed.panelOrder || ALL_PANELS;
        const missing = ALL_PANELS.filter(k => !order.includes(k));
        return {
          panelOrder: [...order.filter(k => ALL_PANELS.includes(k)), ...missing],
          hiddenPanels: parsed.hiddenPanels || [],
          hiddenKpis: parsed.hiddenKpis || [],
        };
      }
      // Migrate from old panel-order key
      const oldOrder = localStorage.getItem('dashboard-panel-order');
      if (oldOrder) {
        const parsed = JSON.parse(oldOrder);
        if (Array.isArray(parsed)) {
          localStorage.removeItem('dashboard-panel-order');
          return { panelOrder: parsed, hiddenPanels: [], hiddenKpis: [] };
        }
      }
    } catch { /* ignore */ }
    return { panelOrder: ALL_PANELS, hiddenPanels: [], hiddenKpis: [] };
  });
  const [showSettings, setShowSettings] = useState(false);

  const saveConfig = useCallback((cfg) => {
    setDashConfig(cfg);
    localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
  }, []);

  const movePanel = useCallback((key, direction) => {
    setDashConfig(prev => {
      const visible = prev.panelOrder.filter(k => !prev.hiddenPanels.includes(k));
      const idx = visible.indexOf(key);
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= visible.length) return prev;
      // Swap in full order
      const fullIdx = prev.panelOrder.indexOf(visible[idx]);
      const fullNewIdx = prev.panelOrder.indexOf(visible[newIdx]);
      const next = [...prev.panelOrder];
      [next[fullIdx], next[fullNewIdx]] = [next[fullNewIdx], next[fullIdx]];
      const cfg = { ...prev, panelOrder: next };
      localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
      return cfg;
    });
  }, []);

  const setPanelOrder = useCallback((newOrder) => {
    setDashConfig(prev => {
      // newOrder is the new visible order; hidden panels keep their relative position appended at end
      const hidden = prev.panelOrder.filter(k => prev.hiddenPanels.includes(k));
      const next = [...newOrder, ...hidden.filter(k => !newOrder.includes(k))];
      const cfg = { ...prev, panelOrder: next };
      localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
      return cfg;
    });
  }, []);

  const togglePanel = useCallback((key) => {
    setDashConfig(prev => {
      const hidden = prev.hiddenPanels.includes(key)
        ? prev.hiddenPanels.filter(k => k !== key)
        : [...prev.hiddenPanels, key];
      const cfg = { ...prev, hiddenPanels: hidden };
      localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
      return cfg;
    });
  }, []);

  const toggleKpi = useCallback((key) => {
    setDashConfig(prev => {
      const hidden = prev.hiddenKpis.includes(key)
        ? prev.hiddenKpis.filter(k => k !== key)
        : [...prev.hiddenKpis, key];
      const cfg = { ...prev, hiddenKpis: hidden };
      localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
      return cfg;
    });
  }, []);

  const resetConfig = useCallback(() => {
    const cfg = { panelOrder: ALL_PANELS, hiddenPanels: [], hiddenKpis: [] };
    saveConfig(cfg);
  }, [saveConfig]);

  const visiblePanels = dashConfig.panelOrder.filter(k => !dashConfig.hiddenPanels.includes(k));

  // Drag-and-drop state for widgets
  const [dragSource, setDragSource] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);

  const handleDrop = useCallback((targetKey) => {
    if (!dragSource || dragSource === targetKey) { setDragSource(null); setDragTarget(null); return; }
    setDashConfig(prev => {
      const order = [...prev.panelOrder];
      const srcIdx = order.indexOf(dragSource);
      const tgtIdx = order.indexOf(targetKey);
      if (srcIdx < 0 || tgtIdx < 0) return prev;
      // Remove source and insert at target position
      order.splice(srcIdx, 1);
      order.splice(tgtIdx, 0, dragSource);
      const cfg = { ...prev, panelOrder: order };
      localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
      return cfg;
    });
    setDragSource(null);
    setDragTarget(null);
  }, [dragSource]);
  const visibleKpis = ALL_KPIS.filter(k => !dashConfig.hiddenKpis.includes(k));

  // Define all panels as a map of key -> JSX
  const panelMap = {
    burn: (props) => <BurnRatePanel
      burnMonths={burnMonths} burnValuesNet={burnValuesNet} burnValuesOperating={burnValuesOperating}
      closedOperating={closedOperating} inProgressLabels={inProgressLabels}
      burnInflows={burnInflows} eomBalances={eomBalances} cc={cc} t={t} {...props} />,
    budget: (props) => <BudgetUtilSection deptUtil={deptUtil} cc={cc} t={t} onNavigate={onNavigate} {...props} />,
    gic: (props) => {
      const BankBadge = ({ bank }) => {
        const isDesj = bank.includes('Desjardins');
        const isRBC = bank === 'RBC';
        const isVM = bank.startsWith('VM');
        const label = isRBC ? 'RBC' : isVM ? 'VM' : isDesj ? 'Desj.' : bank;
        const bg = isRBC ? '#e3eaf3' : isDesj ? '#e4f0e8' : t.borderLight;
        const fg = isRBC ? '#3b6a96' : isDesj ? '#3d7d56' : t.textSecondary;
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            height: 20, padding: '0 8px', borderRadius: 6,
            fontSize: 9, fontWeight: 600, fontFamily: sans,
            background: bg, color: fg, letterSpacing: 0.2,
          }}>{label}</span>
        );
      };
      return (
      <DashSection title="GIC & Investment Maturities" span={2} {...props}>
        <TableRow header cells={[
          { value: 'Bank', width: '70px' },
          { value: 'Curr', width: '36px' },
          { value: 'Amount', align: 'right' },
          { value: 'Rate', align: 'right', width: '60px' },
          { value: 'Int. @ Mat.', align: 'right', width: '80px' },
          { value: 'Maturity', align: 'right', width: '80px' },
        ]} />
        {sortedGics.map((g, i) => {
          // Convert to display currency
          const displayCurr = cc.currency || 'CAD';
          const fxRate = cc.fxRate || 1.3718;
          let displayAmt = g.amount;
          if (g.currency === 'USD' && displayCurr === 'CAD') displayAmt = g.amount * fxRate;
          else if (g.currency === 'CAD' && displayCurr === 'USD') displayAmt = g.amount / fxRate;
          let intLabel = '\u2014';
          if (g.interestAtMaturity != null) {
            let displayInt = g.interestAtMaturity;
            if (g.currency === 'USD' && displayCurr === 'CAD') displayInt = displayInt * fxRate;
            else if (g.currency === 'CAD' && displayCurr === 'USD') displayInt = displayInt / fxRate;
            intLabel = fmtCompactC(displayInt, { currency: 'CAD' });
          }
          return (
          <TableRow key={i} cells={[
            { value: <BankBadge bank={g.bank} />, width: '70px' },
            { value: g.currency, width: '36px' },
            { value: fmtCompactC(displayAmt, { currency: 'CAD' }), align: 'right', mono: true },
            { value: g.rate, align: 'right', mono: true, width: '60px' },
            { value: intLabel, align: 'right', mono: true, width: '80px', color: g.interestAtMaturity != null ? green : undefined },
            { value: g.maturity ? g.maturity.slice(5) : 'Savings', align: 'right', mono: true, width: '80px', color: getMaturityColor(g.maturity) },
          ]} />
          );
        })}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, fontFamily: mono }}>
          <span style={{ color: t.textMuted }}>Accrued Interest (12500): <span style={{ color: green }}>{fmtC(Math.round(accruedInterest), cc)}</span> <span style={{ fontSize: 9 }}>(as of today)</span></span>
          <span style={{ color: t.textMuted }}>Total: {fmtCompactC(gics.reduce((s, g) => {
            const displayCurr = cc.currency || 'CAD';
            const fxRate = cc.fxRate || 1.3718;
            let amt = g.amount;
            if (g.currency === 'USD' && displayCurr === 'CAD') amt = g.amount * fxRate;
            else if (g.currency === 'CAD' && displayCurr === 'USD') amt = g.amount / fxRate;
            return s + amt;
          }, 0), { currency: 'CAD' })}</span>
        </div>
        <div style={{ marginTop: 4, fontSize: 9, color: t.textFaint, fontFamily: sans }}>
          Savings rates: CAD Prime {cadPrime.toFixed(2)}% - 1.75% | USD Prime {usPrime.toFixed(2)}% - 3.50%
        </div>
      </DashSection>
    );},
    fx: (props) => <FxRateTracker usdAssets={usdAssets} cc={cc} t={t} onRateLoaded={onLiveRateLoaded} {...props} />,
    receivables: (props) => (
      <DashSection title="Grants & Receivables" span={2} {...props}>
        <TableRow header cells={[
          { value: 'Account', width: '1fr' },
          { value: 'Code', width: '80px', align: 'center' },
          { value: 'Balance', align: 'right', width: '100px' },
        ]} />
        {receivables.map((r, i) => (
          <TableRow key={i} cells={[
            { value: r.name, width: '1fr' },
            { value: r.code, width: '80px', align: 'center', color: t.textFaint },
            { value: fmtC(r.amount, cc), align: 'right', width: '100px', mono: true, color: green },
          ]} />
        ))}
        <div style={{ marginTop: 8, fontSize: 11, color: t.textMuted, fontFamily: mono, textAlign: 'right' }}>
          Total: {fmtC(receivables.reduce((s, r) => s + r.amount, 0), cc)}
        </div>
      </DashSection>
    ),
    ap: (props) => {
      const overdueToShow = showAllOverdue ? apOverdue : apOverdue.slice(0, 5);
      const bucketColors = { 'Current': green, '31-60': amber, '61-90': red, '91-120': red, '121+': red };
      return (
      <DashSection title="Accounts Payable Aging" span={2} {...props}>
        {/* KPI header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>
              {fmtC(toDisplayAmount(apTotal), { currency: 'CAD' })}
            </div>
            <div style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, marginTop: 1 }}>
              Total AP ({displayCurr})
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: apCurrentPct >= 80 ? green : apCurrentPct >= 60 ? amber : red }}>
              {apCurrentPct}%
            </div>
            <div style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, marginTop: 1 }}>
              current
            </div>
            <div style={{ fontSize: 10, fontFamily: mono, color: apPastDue > 0 ? red : green, marginTop: 2 }}>
              {fmtCompactC(toDisplayAmount(apPastDue), { currency: 'CAD' })} past due
            </div>
          </div>
        </div>

        {/* Aging buckets row */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 10 }}>
          {apAging.map((a, i) => {
            const color = bucketColors[a.bucket];
            const isExpanded = expandedBucket === a.bucket;
            const hasVendors = apBucketVendors[a.bucket] && apBucketVendors[a.bucket].length > 0;
            return (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div
                  onClick={() => hasVendors && setExpandedBucket(isExpanded ? null : a.bucket)}
                  style={{
                    padding: '6px 2px',
                    cursor: hasVendors ? 'pointer' : 'default',
                    borderBottom: `2px solid ${a.amount > 0 ? color : t.borderLight}`,
                    background: isExpanded ? (color + '12') : 'transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontSize: 8, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>
                    {a.bucket}
                  </div>
                  <div style={{ fontSize: 11, fontFamily: mono, fontWeight: 600, color: a.amount > 0 ? color : t.textMuted }}>
                    {a.amount !== 0 ? fmtCompactC(toDisplayAmount(a.amount), { currency: 'CAD' }) : '--'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Expanded bucket vendor breakdown */}
        {expandedBucket && apBucketVendors[expandedBucket] && (
          <div style={{
            background: t.bg,
            border: `1px solid ${t.borderLight}`,
            borderRadius: 6,
            padding: '8px 10px',
            marginBottom: 10,
          }}>
            <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted, marginBottom: 4, fontFamily: sans }}>
              {expandedBucket} bucket
            </div>
            {/* Column headers */}
            <div style={{ display: 'flex', gap: 4, padding: '2px 0', borderBottom: `1px solid ${t.border}`, marginBottom: 2 }}>
              <span style={{ flex: 1, fontSize: 8, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase' }}>Vendor</span>
              <span style={{ width: 30, fontSize: 8, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', textAlign: 'center' }}>Curr</span>
              <span style={{ width: 65, fontSize: 8, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', textAlign: 'right' }}>Original</span>
              <span style={{ width: 65, fontSize: 8, fontFamily: sans, color: t.textMuted, textTransform: 'uppercase', textAlign: 'right' }}>{displayCurr}</span>
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
              {apBucketVendors[expandedBucket].sort((a, b) => b.amount - a.amount).map((v, i) => (
                <div key={i} style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '3px 0', borderBottom: `1px solid ${t.borderLight}` }}>
                  <span style={{ flex: 1, fontSize: 10, fontFamily: sans, color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.vendor}</span>
                  <span style={{ width: 30, fontSize: 9, fontFamily: mono, color: t.textFaint, textAlign: 'center' }}>{v.origCurrency}</span>
                  <span style={{ width: 65, fontSize: 10, fontFamily: mono, color: t.textSecondary, textAlign: 'right' }}>{fmtCompactC(toOrigAmount(v.amount, v.origCurrency), { currency: 'CAD' })}</span>
                  <span style={{ width: 65, fontSize: 10, fontFamily: mono, color: v.amount < 0 ? green : t.textSecondary, textAlign: 'right' }}>{fmtCompactC(toDisplayAmount(v.amount), { currency: 'CAD' })}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action needed — overdue vendors */}
        <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted, marginBottom: 4, fontFamily: sans }}>
          Action needed
        </div>
        {overdueToShow.map((v, i) => {
          const bucket = v.past121 ? '121+' : '31-60';
          const origCurr = getOrigCurrency(v.vendor);
          const cadAmt = (v.past31 || 0) + (v.past121 || 0);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0', borderBottom: `1px solid ${t.borderLight}` }}>
              <div style={{ width: 3, height: 3, borderRadius: '50%', background: bucketColors[bucket], flexShrink: 0 }} />
              <span style={{ fontSize: 10, fontFamily: sans, color: t.textPrimary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.vendor}</span>
              <span style={{ fontSize: 9, fontFamily: mono, color: t.textFaint, flexShrink: 0, width: 28, textAlign: 'center' }}>{origCurr}</span>
              <span style={{ fontSize: 10, fontFamily: mono, color: red, flexShrink: 0 }}>{fmtC(toDisplayAmount(cadAmt), { currency: 'CAD' })}</span>
              <span style={{ fontSize: 8, fontFamily: sans, color: t.textMuted, flexShrink: 0 }}>({bucket})</span>
            </div>
          );
        })}
        {apOverdue.length > 5 && (
          <div
            onClick={() => setShowAllOverdue(!showAllOverdue)}
            style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, cursor: 'pointer', marginTop: 6, textAlign: 'center' }}
          >
            {showAllOverdue ? 'Show less' : `Show ${apOverdue.length - 5} more...`}
          </div>
        )}
      </DashSection>
      );
    },
    close: (props) => (
      <DashSection title={`Month-End Close · ${closeMonthLabel}`} span={4} {...props}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ height: 8, background: t.borderLight, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${closePct}%`, background: green, borderRadius: 4, transition: 'width 0.3s' }} />
            </div>
          </div>
          <span style={{ fontSize: closePct === 0 ? 12 : 16, fontWeight: 700, fontFamily: closePct === 0 ? sans : mono, color: closePct === 100 ? green : closePct === 0 ? t.textMuted : t.textPrimary }}>
        {closePct === 0 ? tr('dash.notStarted') : `${closePct}%`}
      </span>
          <div style={{ display: 'flex', gap: 12 }}>
            <span style={{ fontSize: 11, fontFamily: sans, color: t.textMuted }}>Done: <strong style={{ color: green }}>{done}</strong></span>
            <span style={{ fontSize: 11, fontFamily: sans, color: t.textMuted }}>Reviewed: <strong style={{ color: green }}>{reviewed}</strong></span>
            <span style={{ fontSize: 11, fontFamily: sans, color: t.textMuted }}>Overdue: <strong style={{ color: overdue > 0 ? red : green }}>{overdue}</strong></span>
            <span style={{ fontSize: 11, fontFamily: sans, color: t.textMuted }}>Blocked: <strong style={{ color: blocked > 0 ? amber : green }}>{blocked}</strong></span>
          </div>
        </div>
      </DashSection>
    ),
  };

  // KPI definitions keyed for visibility filtering
  const kpiDefs = {
    cash: <MiniKpi icon={DollarSign} label="Cash & Investments" value={fmtCompactC(totalLiquidity, cc)} sub={`Cash: ${fmtCompactC(cashInBank, cc)}`} color={green} />,
    burn: <MiniKpi icon={Flame} label="Monthly Burn" value={fmtCompactC(monthlyBurn, cc)} sub="Avg 6-month" color={red} />,
    runway: <MiniKpi
      icon={Clock}
      label="Runway"
      value={runwayProjection.cashOutLabel || 'Beyond 2031'}
      sub={
        runwayProjection.monthsUntil != null
          ? `${runwayProjection.monthsUntil} mo · pre-Series B`
          : 'Forecast covers full window'
      }
      color={runwayColor} />,
    budgetUtil: <MiniKpi icon={BarChart3} label="Budget Utilization" value="85%" sub="All departments" color={green} />,
    fxExposure: <MiniKpi icon={ArrowLeftRight} label="FX Exposure" value={fmtCompactC(usdAssets, cc)} sub={`Unrealized: ${fmtCompactC(unrealizedFX, cc)}`} color={amber} />,
    monthEnd: <MiniKpi icon={ClipboardCheck} label={`Month-End · ${closeMonthLabel}`} value={closePct === 0 ? tr('dash.notStarted') : `${closePct}%`} sub={closePct === 0 ? tr('dash.noTasksCompleted') : `${done}/${total} done`} color={closePct === 100 ? green : closePct === 0 ? t.textMuted : amber} />,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
      {/* Settings modal */}
      {showSettings && (
        <DashboardSettings
          config={dashConfig}
          panelLabels={PANEL_LABELS}
          kpiLabels={KPI_LABELS}
          allPanels={ALL_PANELS}
          allKpis={ALL_KPIS}
          onTogglePanel={togglePanel}
          onToggleKpi={toggleKpi}
          onMovePanel={movePanel}
          onSetPanelOrder={setPanelOrder}
          onReset={resetConfig}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Gear icon — top right of dashboard */}
      <div style={{ gridColumn: 'span 4', display: 'flex', justifyContent: 'flex-end', marginBottom: -8 }}>
        <button onClick={() => setShowSettings(true)} title="Customize dashboard" style={{
          background: 'none', border: `1px solid ${t.borderLight}`, borderRadius: 6,
          cursor: 'pointer', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 6,
          color: t.textMuted, fontSize: 11, fontFamily: sans,
          transition: 'all 0.15s',
        }}>
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="10" cy="10" r="3" />
            <path d="M10 1v2M10 17v2M1 10h2M17 10h2M3.5 3.5l1.4 1.4M15.1 15.1l1.4 1.4M3.5 16.5l1.4-1.4M15.1 4.9l1.4-1.4" />
          </svg>
          Customize
        </button>
      </div>

      {/* KPI strip — individual cards like investor hero */}
      {visibleKpis.length > 0 && (
        <div style={{
          gridColumn: 'span 4',
          display: 'grid', gridTemplateColumns: `repeat(${visibleKpis.length}, 1fr)`,
          gap: 14,
        }}>
          {visibleKpis.map((key) => (
            <React.Fragment key={key}>
              {kpiDefs[key]}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Ordered + filtered panels — drag to reorder */}
      {visiblePanels.length > 0 ? visiblePanels.map((key, idx) => {
        const render = panelMap[key];
        if (!render) return null;
        return <React.Fragment key={key}>{render({
          dragKey: key,
          onDragStart: setDragSource,
          onDragOver: setDragTarget,
          onDrop: handleDrop,
          isDragOver: dragTarget === key && dragSource !== key,
        })}</React.Fragment>;
      }) : (
        <div style={{
          gridColumn: 'span 4', textAlign: 'center', padding: 40,
          color: t.textMuted, fontSize: 13, fontFamily: sans,
        }}>
          No widgets selected. Click <strong>Customize</strong> to add widgets to your dashboard.
        </div>
      )}
    </div>
  );
}

// ─── Main Dashboard Component ───────────────────────────────────────────────────
export default function Dashboard({ monthData, taskList, mockToday, onNavigate, sbData, onLiveRateLoaded }) {
  return <ControllerDashboard monthData={monthData} taskList={taskList} mockToday={mockToday} onNavigate={onNavigate} sbData={sbData} onLiveRateLoaded={onLiveRateLoaded} />;
}
